import getReport, { createReport } from './report/create'
import { $t, LocalContext } from './i18n/LocalContext'
import Home from './pages/Home/Home'
import { StateContext, DispatchContext, useDispatch } from './store'
import { StrictMode, Suspense, lazy, useEffect, useRef, useState } from 'react'
import Toast from './components/Toast/Toast'
import Pending from './components/Pending/Pending'
import useUserInfo from './hooks/useUserInfo'
import langJson from './i18n/index.json'
import KowNav from './components/KowNav/KowNav'
import { throwError, toast } from './utils'
import {
  setToken,
  loginMessageIsString,
  clearToken,
  getToken,
} from './request/utils'
import { emitter } from './emitter'
import { Handler } from 'mitt'
import { obj2query, query2obj } from '@base/lang'
import CommonModal from './components/CommonModal/CommonModal'
import SelectRole from './components/SelectRole/SelectRole'
import Alert from './components/Alert/Alert'
import Record from './components/Record/Record'
import RewardPreview from './components/RewardPreview/RewardPreview'
import BindCode from './components/BindCode/BindCode'
import Share from './components/Share/Share'

createReport()
const $report = getReport()
$report({
  name: 'h5EventLaunch',
  round: window.inSDK ? 1 : 2,
})

function Root() {
  const { lang, gamePlayer, sdkConfig, userInfo } = useUserInfo()
  const { state, dispatch } = useDispatch()
  const openWebLogin = state.openWebLogin
  const [local, setLocal] = useState({})
  const [ready, setReady] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const changeLoginStatus = (data: { logined: boolean; token: string }) => {
    dispatch({
      type: 'login',
      data,
    })
  }

  const closeWebLogin = () => {
    dispatch({
      type: 'openWebLogin',
      data: {
        openWebLogin: false,
      },
    })
  }

  // 自动登录
  useEffect(() => {
    if (lang) {
      const _local = langJson[lang as keyof typeof langJson]
      window.local = _local
      setLocal(_local)

      if (!window.inSDK) {
        iframeRef.current = document.createElement('iframe')
        iframeRef.current.id = 'iframe'
        iframeRef.current.src = `${
          process.env.ENV !== 'production'
            ? 'https://passportbetaouter.onemt.com/'
            : 'https://passport.onemt.com/'
        }?lang=${lang}&game=kow&area=${
          window.queryObj.area
        }&embed=true&redirect=${encodeURIComponent(location.href)}`
        iframeRef.current.style.display = 'none'
        window.webLoginLoad$ = new Promise<void>((resolve) => {
          const timeout = setTimeout(() => {
            resolve()
          }, 5000)
          iframeRef.current.addEventListener('load', () => {
            clearTimeout(timeout)
            resolve()
          })
        })
        window.load$.then(() => {
          if (!document.getElementById('iframe')) {
            document.body.appendChild(iframeRef.current)
          }
        })
      }

      const urlToken = window.queryObj.token
      let errmsg = window.queryObj.errmsg
      const hasUrlToken =
        typeof urlToken === 'string' &&
        urlToken.length > 0 &&
        !['null', 'undefined'].includes(urlToken)
      const hasErrmsg = typeof errmsg === 'string' && errmsg.length > 0
      if (hasErrmsg) {
        errmsg = decodeURIComponent(errmsg)
        const _handle = () => {
          console.log('openWebLogin')
          dispatch({
            type: 'openWebLogin',
            data: {
              openWebLogin: true,
            },
          })
        }
        dispatch({
          type: 'setAlertData',
          data: {
            open: true,
            content: errmsg,
            resolveClose: true,
            onResolve: () => {
              _handle()
            },
            onReject: () => {
              _handle()
            },
          },
        })
      }

      try {
        // 第三方登录
        if (hasUrlToken) {
          const token = decodeURIComponent(urlToken)
          setToken({ token })
        }
      } catch (error) {
        throwError(error)
      }

      const fbclid = window.queryObj.fbclid
      const hasFbclid = typeof fbclid === 'string' && fbclid.length > 0
      if ('history' in window && (hasUrlToken || hasErrmsg || hasFbclid)) {
        const newQueryObj: Window['queryObj'] = query2obj(location.search)
        delete newQueryObj.token
        delete newQueryObj.errmsg
        delete newQueryObj.fbclid
        const newHref = `${location.protocol}//${location.host}${
          location.pathname
        }${obj2query(newQueryObj)}`
        window.history.pushState({ urlToken }, '', newHref)
      }

      userInfo.sessionId = getToken()

      dispatch({
        type: 'setLoginData',
        data: {
          lang,
          logined: !!getToken(),
          gamePlayer,
          sdkConfig,
          userInfo,
        },
      })
      setReady(true)

      document.title = _local['回归夺宝']

      const appShellPending = document.querySelector('.app-shell-pending')
      if (appShellPending) {
        appShellPending.remove()
      }
    }
  }, [lang])

  // web登录
  useEffect(() => {
    const iframeElem = iframeRef.current

    const _messageHandle = (e: MessageEvent<LoginMessage>) => {
      const type = e.data.type

      if (type == null) return

      switch (e.data.type) {
        case 'login':
          if (loginMessageIsString(e.data.data)) break
          try {
            const loginData = e.data.data
            setToken(loginData)
            changeLoginStatus({
              logined: true,
              token: getToken(),
            })
            closeWebLogin()
          } catch (error) {
            throwError(error)
          }
          break
        case 'close':
          closeWebLogin()
          break
        case 'openUrl':
          if (loginMessageIsString(e.data.data)) {
            window.location.href = e.data.data
          }
          break
      }
    }

    const _cancel = () => {
      window.removeEventListener('message', _messageHandle)
    }

    if (!openWebLogin) {
      if (iframeElem) {
        iframeElem.style.display = 'none'
      }
      return _cancel
    }

    if (!document.getElementById('iframe')) {
      document.body.appendChild(iframeElem)
    }
    if (iframeElem) {
      window.addEventListener('message', _messageHandle)
      iframeElem.style.display = 'block'
    }

    return _cancel
  }, [openWebLogin])

  // 退出登录
  useEffect(() => {
    const _handler: Handler<LogoutEvent> = (e) => {
      const { alert, rejectClose, go2login } = e

      let text = ''
      if (typeof alert === 'string') {
        text = alert
      }

      const _clearData = () => {
        clearToken()
        dispatch({
          type: 'logout',
        })
        dispatch({
          type: 'closeModal',
        })
        toast($t('退出成功'), true)
        if (go2login && !window.inSDK) {
          setTimeout(() => {
            dispatch({
              type: 'openWebLogin',
              data: {
                openWebLogin: true,
              },
            })
          }, 1)
        }
      }

      if (alert) {
        return setTimeout(() => {
          dispatch({
            type: 'setAlertData',
            data: {
              open: true,
              rejectText: $t('取消'),
              content: text,
              rejectClose,
              onResolve: () => {
                _clearData()
              },
              onReject: () => {
                if (!rejectClose) return

                _clearData()
              },
            },
          })
        }, 1)
      }

      _clearData()
    }

    emitter.on('logout', _handler)

    return () => {
      emitter.off('logout', _handler)
    }
  }, [])

  // loading
  useEffect(() => {
    const _open = () => {
      dispatch({
        type: 'setPendingData',
        data: {
          open: true,
        },
      })
    }

    const _close = () => {
      dispatch({
        type: 'setPendingData',
        data: {
          open: false,
        },
      })
    }

    emitter.on('loading', _open)
    emitter.on('closeLoading', _close)

    const _cancel = () => {
      emitter.off('loading', _open)
      emitter.off('closeLoading', _close)
    }

    return _cancel
  }, [])

  // 加载异步组件
  // const Share = lazy(() => import('./components/Share/Share'))

  return ready ? (
    <LocalContext.Provider value={local}>
      <StateContext.Provider value={state}>
        <DispatchContext.Provider value={dispatch}>
          <KowNav />
          <Home />
          <Pending />
          <Toast />
          <CommonModal
            open={state.modalData.ruleOpen}
            title={$t('活动规则')}
            type="rule"
            length="long"
          >
            {[
              <div key="1" className="rule-modal-body">
                <p
                  className="common-modal-text"
                  dangerouslySetInnerHTML={{ __html: $t('活动规则内容') }}
                ></p>
              </div>,
            ]}
          </CommonModal>
          <SelectRole />
          <Alert />
          <Record />
          <RewardPreview />
          <BindCode />
          <Share />
          {/* <Suspense fallback={null}>
            <Share />
          </Suspense> */}
        </DispatchContext.Provider>
      </StateContext.Provider>
    </LocalContext.Provider>
  ) : null
}

export default function App() {
  return (
    <StrictMode>
      <Root />
    </StrictMode>
  )
}
